import { postApi } from "utils/apiCall";
import cleaningAuditClient from "./cleaningAuditClient";
import { CLIENT_ID } from "constants/ApiEndPointsConstants";

// module_id "18" matches the mapping in src/components/layout-components/MenuContent.js's
// handleExternalClick, which is what mints this same handoff token for the old
// window.open flow.
const CLEANING_AUDIT_MODULE_ID = "18";

// Getting into Cleaning Audit is a two-step handoff, not a single token forward:
//
// 1. MyM's OWN backend (not Cleaning Audit's) mints a fresh, one-time, module-scoped
//    token via /auto_login_app_web — see src/redux/actions/postAutoLogin.action.js.
//    This is NOT the same as MyM's regular session token (localStorage "token");
//    it's a separate handoff token generated specifically for this purpose. Sending
//    the regular session token here 401s, because Cleaning Audit's own user/token
//    table has never seen it.
// 2. That handoff token is exchanged with Cleaning Audit's /cleaning_auto_login for
//    a Cleaning-Audit-specific session token, which is what every further call uses.
//
// Known issue (as of this writing): the Cleaning Audit backend's token-issuing path
// appears to invalidate/replace the previously issued token on every call for the
// same user (confirmed by direct DB inspection), so a session minted here can go
// stale if another auto-login for the same user happens concurrently elsewhere.
// Not something fixable from this side — flagged for whoever owns that API.
//
// Run both steps silently here, once per browser session, and keep the result only
// in memory (never localStorage) so it can't collide with or get cleared alongside
// MyM's own "token"/"client_id" keys.
let cachedSessionPromise = null;
// Which MyM user/client the cached session belongs to. Sign-out/sign-in
// doesn't reload the page, so without this a manager signing in after an
// admin in the same tab would keep using the admin's module session.
let cachedSessionOwner = null;
const currentSessionOwner = () => `${localStorage.getItem("user_id")}|${localStorage.getItem("client_id")}`;

const getHandoffToken = async () => {
  const userId = localStorage.getItem("user_id");
  const { error, result } = await postApi("/auto_login_app_web", {
    module_id: CLEANING_AUDIT_MODULE_ID,
    user_id: userId,
  });
  const handoffToken = result?.result?.token_id;
  if (error || !handoffToken) {
    throw new Error("Could not obtain a Cleaning Audit handoff token from MyM.");
  }
  return handoffToken;
};

const exchangeToken = async () => {
  const handoffToken = await getHandoffToken();
  const { data } = await cleaningAuditClient.post(
    "/cleaning_auto_login",
    { token_id: handoffToken },
    {
      headers: {
        "token-id": handoffToken,
        "device-type": process.env.REACT_APP_CA_DEVICE_TYPE,
      },
    }
  );
  const caTokenId = data?.result?.token_id;
  if (!caTokenId) {
    throw new Error("Cleaning Audit login did not return a token.");
  }
  // role_id comes back in this same response (Cleaning Audit's own role for this
  // user, resolved server-side) — screens that need it (e.g. the state-by-role
  // lookup) read it from here instead of a separate call.
  return {
    tokenId: caTokenId,
    clientId: localStorage.getItem(CLIENT_ID),
    roleId: data?.result?.role_id,
  };
};

export const getCleaningAuditSession = () => {
  if (cachedSessionOwner !== currentSessionOwner()) {
    cachedSessionPromise = null;
    cachedSessionOwner = currentSessionOwner();
  }
  if (!cachedSessionPromise) {
    cachedSessionPromise = exchangeToken().catch((error) => {
      cachedSessionPromise = null; // let the next call retry instead of caching a failure
      throw error;
    });
  }
  return cachedSessionPromise;
};

// Cleaning Audit's own token-issuing path can invalidate a session we already
// hold (observed directly against the dev DB — each new auto-login for the same
// user rotates the token in place, so a concurrent login elsewhere goes stale).
// Callers that get a 401 on an otherwise-valid session should call this, then
// retry once through getCleaningAuditSession() to force a fresh exchange.
export const invalidateCleaningAuditSession = () => {
  cachedSessionPromise = null;
};
